import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { trackEvent } from "@/components/Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = ["Advogado", "Advogado/Estagiário", "Consultor", "Jurista"];

const Recrutamento = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [form, setForm] = useState({
        full_name: "",
        address: "",
        address_2: "",
        city: "",
        postal_code: "",
        phone: "",
        email: "",
        position: POSITIONS[0],
        cv_url: "",
        message: "",
    });
    const [rgpd, setRgpd] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!rgpd) {
            toast.error(
                "Precisa de aceitar o armazenamento das suas informações (RGPD).",
            );
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/recruitment`, form);
            trackEvent("recruitment_form_submit", { position: form.position });
            toast.success(
                "Candidatura enviada com sucesso! Entraremos em contacto brevemente.",
            );
            setForm({
                full_name: "",
                address: "",
                address_2: "",
                city: "",
                postal_code: "",
                phone: "",
                email: "",
                position: POSITIONS[0],
                cv_url: "",
                message: "",
            });
            setRgpd(false);
        } catch (err) {
            toast.error("Ocorreu um erro. Tente novamente.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-recrutamento">
            <SEO
                title="Recrutamento"
                description="Candidate-se à ACD Advogados. Procuramos advogados, advogados estagiários, consultores e juristas com formação sólida e domínio de línguas estrangeiras."
                canonicalPath="/recrutamento"
            />
            <section className="relative py-20 md:py-28 px-6 bg-[var(--acd-navy-900)] text-white overflow-hidden">
                <div className="absolute inset-0 opacity-25 bg-[url('https://customer-assets.emergentagent.com/job_advogados-staging/artifacts/nd0v3cg2_pexels-su-lopes-54155462-7789476.webp')] bg-cover bg-center" />
                <div className="absolute inset-0 bg-gradient-to-r from-[var(--acd-navy-950)]/95 to-[var(--acd-navy-900)]/70" />
                <div className="relative z-10 max-w-7xl mx-auto text-center">
                    <span className="acd-eyebrow">Recrutamento</span>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mt-3">
                        Política de Recrutamento
                    </h1>
                </div>
            </section>

            <section className="py-20 px-6">
                <div className="max-w-4xl mx-auto">
                    <p className="text-[color:var(--acd-muted)] leading-relaxed mb-4">
                        Na sua maioria, os advogados que trabalham na Ana Cristina Domingues Advogados fizeram o seu estágio nestes escritórios. A seleção dos nossos estagiários tem em perspetiva, em muitos casos, a sua permanência, prevendo já a sua formação para as necessidades específicas desta organização.
                    </p>
                    <p className="text-[color:var(--acd-muted)] leading-relaxed mb-4">
                        Os nossos critérios de recrutamento de advogados e advogados estagiários assentam na busca de profissionais com formação sólida, capacidade para trabalhar em equipa, domínio de línguas estrangeiras (o Inglês o Francês são indispensáveis) e com conhecimento dos princípios deontológicos que regem a profissão.
                    </p>
                    <p className="text-[color:var(--acd-muted)] leading-relaxed mb-8">
                        O processo de seleção é simples, desburocratizado e contínuo, consistindo fundamentalmente no preenchimento do formulário de candidatura e na realização de uma entrevista.
                    </p>

                    <h2 className="text-3xl font-bold text-[var(--acd-navy-900)] mb-6">
                        Formulário de Candidatura
                    </h2>

                    <form
                        onSubmit={submit}
                        className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-4"
                        data-testid="recruitment-form"
                    >
                        <div>
                            <label className="acd-label">Nome Completo *</label>
                            <input
                                className="acd-input"
                                required
                                value={form.full_name}
                                onChange={set("full_name")}
                                data-testid="rec-name"
                            />
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="acd-label">Morada</label>
                                <input
                                    className="acd-input"
                                    value={form.address}
                                    onChange={set("address")}
                                    data-testid="rec-address"
                                />
                            </div>
                            <div>
                                <label className="acd-label">
                                    Continuação da morada
                                </label>
                                <input
                                    className="acd-input"
                                    value={form.address_2}
                                    onChange={set("address_2")}
                                    data-testid="rec-address2"
                                />
                            </div>
                            <div>
                                <label className="acd-label">Cidade</label>
                                <input
                                    className="acd-input"
                                    value={form.city}
                                    onChange={set("city")}
                                    data-testid="rec-city"
                                />
                            </div>
                            <div>
                                <label className="acd-label">Código Postal</label>
                                <input
                                    className="acd-input"
                                    value={form.postal_code}
                                    onChange={set("postal_code")}
                                    data-testid="rec-postal"
                                />
                            </div>
                            <div>
                                <label className="acd-label">Contacto *</label>
                                <input
                                    className="acd-input"
                                    required
                                    value={form.phone}
                                    onChange={set("phone")}
                                    data-testid="rec-phone"
                                />
                            </div>
                            <div>
                                <label className="acd-label">E-mail *</label>
                                <input
                                    type="email"
                                    className="acd-input"
                                    required
                                    value={form.email}
                                    onChange={set("email")}
                                    data-testid="rec-email"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="acd-label">
                                Cargo a que se candidata *
                            </label>
                            <select
                                className="acd-select"
                                value={form.position}
                                onChange={set("position")}
                                data-testid="rec-position"
                            >
                                {POSITIONS.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="acd-label">
                                CV e Carta de Motivação (link para PDF) *
                            </label>
                            <input
                                className="acd-input"
                                required
                                placeholder="Ex: link do Google Drive / Dropbox para o PDF"
                                value={form.cv_url}
                                onChange={set("cv_url")}
                                data-testid="rec-cv"
                            />
                            <p className="text-xs text-[color:var(--acd-muted)] mt-1">
                                Partilhe um link público (ou de acesso) para o
                                seu CV em PDF.
                            </p>
                        </div>
                        <div>
                            <label className="acd-label">Mensagem</label>
                            <textarea
                                className="acd-textarea"
                                rows={4}
                                value={form.message}
                                onChange={set("message")}
                                data-testid="rec-message"
                            />
                        </div>
                        <label className="flex items-start gap-3 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={rgpd}
                                onChange={(e) => setRgpd(e.target.checked)}
                                className="mt-1 h-4 w-4 accent-[var(--acd-blue-600)]"
                                data-testid="rec-rgpd"
                            />
                            <span>
                                <strong>RGPD:</strong> Concordo que este site
                                armazene as informações enviadas por mim neste
                                pedido, de forma a que a ACD Advogados possa
                                processar a minha candidatura.
                            </span>
                        </label>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="acd-btn-gradient"
                            data-testid="rec-submit"
                        >
                            {submitting ? "A enviar..." : "Enviar Candidatura"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Recrutamento;
